import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { 
  CreditCard, ExternalLink, Loader2, CheckCircle, Clock, AlertCircle, 
  Send, RefreshCw, ChevronDown, ChevronUp 
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api/pas-moonpay`;

export const AdminMoonPayOrders = ({ token }) => {
  const [orders, setOrders] = useState([]);
  const [summary, setSummary] = useState({});
  const [platformWallet, setPlatformWallet] = useState('');
  const [loading, setLoading] = useState(true);
  const [distributing, setDistributing] = useState(null);
  const [expanded, setExpanded] = useState(true);

  const loadOrders = async () => {
    setLoading(true);
    try {
      const r = await axios.get(`${API}/admin/orders`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setOrders(r.data.orders || []);
      setSummary(r.data.summary || {});
      setPlatformWallet(r.data.platform_wallet || '');
    } catch (e) {
      toast.error('Failed to load MoonPay orders');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { loadOrders(); }, [token]);

  const distributeETH = async (orderId) => {
    setDistributing(orderId);
    try {
      const r = await axios.post(`${API}/admin/distribute`, 
        { order_id: orderId },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success(r.data.message);
      loadOrders();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Distribution failed');
    } finally {
      setDistributing(null);
    }
  };

  const statusBadge = (status) => {
    const styles = {
      pending: 'bg-slate-600 text-slate-200',
      moonpay_completed: 'bg-amber-500/20 text-amber-300 border border-amber-500/50',
      distributing: 'bg-blue-500/20 text-blue-300',
      completed: 'bg-emerald-500/20 text-emerald-300',
      failed: 'bg-red-500/20 text-red-300',
      distribution_failed: 'bg-red-500/20 text-red-300'
    };
    const labels = {
      pending: 'Awaiting Payment',
      moonpay_completed: 'Ready to Distribute',
      distributing: 'Distributing...',
      completed: 'Completed',
      failed: 'Failed',
      distribution_failed: 'Distribution Failed'
    };
    return (
      <span className={`px-2 py-0.5 rounded text-xs font-medium ${styles[status] || styles.pending}`}>
        {labels[status] || status}
      </span>
    );
  };

  const readyCount = summary.ready_to_distribute || 0;

  return (
    <div className="bg-slate-800 border border-slate-700 rounded-xl p-6" data-testid="admin-moonpay-orders">
      <div 
        className="flex items-center justify-between cursor-pointer"
        onClick={() => setExpanded(!expanded)}
      >
        <div className="flex items-center gap-3">
          <CreditCard className="w-5 h-5 text-purple-400" />
          <h2 className="text-white font-semibold">MoonPay Orders</h2>
          {readyCount > 0 && (
            <span className="px-2 py-0.5 bg-amber-500 text-black rounded-full text-xs font-bold">
              {readyCount} ready
            </span>
          )}
        </div>
        <div className="flex items-center gap-3">
          <button 
            onClick={(e) => { e.stopPropagation(); loadOrders(); }}
            className="p-1.5 text-slate-400 hover:text-white rounded"
            data-testid="moonpay-refresh-btn"
          >
            <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
          </button>
          {expanded ? <ChevronUp className="w-4 h-4 text-slate-400" /> : <ChevronDown className="w-4 h-4 text-slate-400" />}
        </div>
      </div>

      {expanded && (
        <>
          {/* Summary */}
          <div className="mt-4 grid grid-cols-2 sm:grid-cols-4 gap-3 text-center">
            <div className="bg-slate-900/50 rounded-lg p-2">
              <p className="text-lg font-bold text-slate-300">{summary.pending || 0}</p>
              <p className="text-xs text-slate-500">Pending</p>
            </div>
            <div className="bg-amber-500/10 border border-amber-500/30 rounded-lg p-2">
              <p className="text-lg font-bold text-amber-300">{summary.ready_to_distribute || 0}</p>
              <p className="text-xs text-amber-400">Ready</p>
            </div>
            <div className="bg-emerald-500/10 rounded-lg p-2">
              <p className="text-lg font-bold text-emerald-300">{summary.completed || 0}</p>
              <p className="text-xs text-slate-500">Completed</p>
            </div>
            <div className="bg-red-500/10 rounded-lg p-2">
              <p className="text-lg font-bold text-red-300">{summary.failed || 0}</p>
              <p className="text-xs text-slate-500">Failed</p>
            </div>
          </div>

          {platformWallet && (
            <p className="mt-3 text-xs text-slate-500">
              Platform wallet: <code className="text-purple-400">{platformWallet}</code>
            </p>
          )}

          {/* Orders Table */}
          {loading ? (
            <div className="mt-4 flex justify-center py-8">
              <Loader2 className="w-6 h-6 animate-spin text-purple-400" />
            </div>
          ) : orders.length === 0 ? (
            <p className="mt-4 text-center text-slate-500 py-8">No MoonPay orders yet</p>
          ) : (
            <div className="mt-4 space-y-2 max-h-96 overflow-y-auto">
              {orders.map(order => (
                <div 
                  key={order.order_id}
                  className={`rounded-lg p-3 ${
                    order.status === 'moonpay_completed' 
                      ? 'bg-amber-500/10 border border-amber-500/30' 
                      : 'bg-slate-900/60'
                  }`}
                  data-testid={`moonpay-admin-order-${order.order_id}`}
                >
                  <div className="flex items-start justify-between">
                    <div>
                      <p className="text-sm font-medium text-white">{order.order_id}</p>
                      <p className="text-xs text-slate-400">{order.client_email}</p>
                    </div>
                    {statusBadge(order.status)}
                  </div>
                  
                  <div className="mt-2 grid grid-cols-2 gap-2 text-xs">
                    <div>
                      <span className="text-slate-500">Amount:</span>{' '}
                      <span className="text-white">${order.usd_amount}</span>
                    </div>
                    <div>
                      <span className="text-slate-500">ETH:</span>{' '}
                      <span className="text-emerald-300">{order.eth_received || '—'}</span>
                    </div>
                    <div className="col-span-2">
                      <span className="text-slate-500">To:</span>{' '}
                      <code className="text-purple-300 text-xs">{order.destination_address?.slice(0, 16)}...</code>
                      <span className="text-slate-500 ml-1">({order.destination_label})</span>
                    </div>
                  </div>

                  {/* Action buttons */}
                  {order.status === 'moonpay_completed' && (
                    <button
                      onClick={() => distributeETH(order.order_id)}
                      disabled={distributing === order.order_id}
                      className="mt-3 w-full px-3 py-2 bg-emerald-500 hover:bg-emerald-600 disabled:opacity-50 text-white rounded-lg text-sm font-medium flex items-center justify-center gap-2"
                      data-testid={`distribute-btn-${order.order_id}`}
                    >
                      {distributing === order.order_id ? (
                        <Loader2 className="w-4 h-4 animate-spin" />
                      ) : (
                        <Send className="w-4 h-4" />
                      )}
                      Distribute {order.eth_received} ETH to Client
                    </button>
                  )}

                  {order.status === 'distribution_failed' && (
                    <div className="mt-2">
                      <p className="text-xs text-red-400">{order.distribution_error}</p>
                      <button
                        onClick={() => distributeETH(order.order_id)}
                        disabled={distributing === order.order_id}
                        className="mt-2 px-3 py-1.5 border border-amber-500 text-amber-300 rounded text-xs"
                      >
                        Retry Distribution
                      </button>
                    </div>
                  )}

                  {order.distribution_explorer_url && (
                    <a 
                      href={order.distribution_explorer_url} 
                      target="_blank" 
                      rel="noreferrer"
                      className="mt-2 text-xs text-purple-400 flex items-center gap-1"
                    >
                      <ExternalLink className="w-3 h-3" /> View on Etherscan
                    </a>
                  )}

                  <p className="mt-2 text-xs text-slate-600">
                    {new Date(order.created_at).toLocaleString()}
                  </p>
                </div>
              ))}
            </div>
          )}
        </>
      )}
    </div>
  );
};
